import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { APP_PATHS, AUTH_PATHS } from '@/app/routes/paths';
import LanguageSwitcher from '@/i18n/LanguageSwitcher';
import { getInitials } from '@/lib/getInitials';
import { pickMediaUrl } from '@/lib/pickMediaUrl';
import { useAuthStore } from '@/store/authStore';
import { useProfileShareStore } from '@/features/profile/profileShareStore';
import { ChevronDownIcon, LogOutIcon, SettingsIcon, ShareIcon, UserIcon } from '@/shared/icons';
import clsx from 'clsx';
import { iconTouchTargetSquareClassName } from '@/shared/config/theme';

const DEMO_USER = {
  name: 'John Doe',
  email: 'john.doe@university.edu',
} as const;

export default function UserProfileMenu() {
  const { t } = useTranslation('userMenu');
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const user = useAuthStore((state) => state.user);
  const rawUser = useAuthStore((state) => state.rawUser);
  const logout = useAuthStore((state) => state.logout);
  const openProfileShare = useProfileShareStore((state) => state.openProfileShare);

  const profile = user ?? DEMO_USER;
  const initials = getInitials(profile.name);

  const avatarUrl =
    pickMediaUrl(
      rawUser,
      'profileImageThumbnail',
      'profileImage',
      'avatarUrl',
      'profilePic',
      'photo',
      'profilePicture',
      'imageUrl',
      'image',
    ) ?? user?.avatarUrl;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const goToProfile = () => {
    setOpen(false);
    navigate(APP_PATHS.profile);
  };

  const goToSettings = () => {
    setOpen(false);
    navigate(APP_PATHS.settings);
  };

  const openShareProfile = () => {
    setOpen(false);
    openProfileShare();
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate(AUTH_PATHS.login);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(iconTouchTargetSquareClassName, 'gap-2 rounded-lg hover:bg-gray-50')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <div
          className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-sm font-semibold text-gray-600 ring-1 ring-gray-200/80"
          aria-hidden
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <ChevronDownIcon
          className={clsx('h-4 w-4 text-gray-500 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <div
          className="absolute end-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border border-gray-300 bg-white py-2 shadow-lg"
          role="menu"
        >
          <div className="border-b border-gray-100 px-4 py-3">
            <div className="mb-2 flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-sm font-semibold text-gray-600 ring-1 ring-gray-200/80">
              {avatarUrl ? (
                <img src={avatarUrl} alt={profile.name} className="h-full w-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <p className="font-semibold text-gray-900">{profile.name}</p>
            <p className="text-sm text-gray-500">{profile.email}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={goToProfile}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <UserIcon className="h-5 w-5 text-gray-500" />
            {t('viewProfile')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={openShareProfile}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <ShareIcon className="h-5 w-5 text-gray-500" />
            {t('shareProfile')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={goToSettings}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <SettingsIcon className="h-5 w-5 text-gray-500" />
            {t('settings')}
          </button>

          <LanguageSwitcher />

          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
          >
            <LogOutIcon className="h-5 w-5" />
            {t('logout')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
